/**
 * Recording from the camera or the microphone straight onto the timeline.
 *
 * The browser does all of it: `getUserMedia` for the devices, `MediaRecorder`
 * for the encoding. What comes out is a file, and a file is what every other
 * source here already is, so the recording joins the project the way a dropped
 * file does: one source, a video clip and an audio clip over it.
 *
 * The one thing a recording lacks is a place on disk, and the project format
 * cannot hold media bytes (see timeline.ts). So the file is written out the
 * moment recording stops: through the save picker where there is one, which
 * hands back a handle the project can reopen from, and as a download elsewhere.
 * Either way the bytes end up in the user's folder, where every other source
 * lives, and the privacy story stays the one on the tin.
 */

import { fingerprintFile } from "./sources.ts";
import { newId, type SourceRef } from "./timeline.ts";
import { withDuration } from "./webm.ts";

export type RecordMode = "camera" | "mic";

export interface Container {
  mimeType: string;
  extension: string;
}

/**
 * The best container this browser can record into. VP9 in WebM where it
 * exists, since the timeline only needs the browser itself to play it back;
 * MP4 on Safari, which records nothing else.
 */
export function pickContainer(mode: RecordMode, supported: (type: string) => boolean): Container | null {
  const candidates =
    mode === "camera"
      ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4"]
      : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4"];
  for (const mimeType of candidates) {
    if (supported(mimeType)) return { mimeType, extension: extensionFor(mimeType) };
  }
  return null;
}

export function extensionFor(mimeType: string): string {
  if (mimeType.startsWith("audio/mp4")) return "m4a";
  if (mimeType.startsWith("video/mp4")) return "mp4";
  return "webm";
}

/** "Camera 2026-09-03 14.02.31.webm": sorts by time and says what it is. */
export function recordingName(mode: RecordMode, extension: string, when = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
  const time = `${pad(when.getHours())}.${pad(when.getMinutes())}.${pad(when.getSeconds())}`;
  return `${mode === "camera" ? "Camera" : "Voice"} ${date} ${time}.${extension}`;
}

/**
 * Where on the timeline the recording's first moment belongs.
 *
 * Playback and the recorder are started in the same breath, but the recorder
 * takes a moment to actually begin, so its first frame corresponds to a little
 * after where the playhead was. Placing the clip that much later keeps a
 * voiceover in step with what was playing when it was spoken.
 */
export function clipStartFor(playheadAt: number, playStartedAt: number | null, recorderStartedAt: number): number {
  // With nothing playing there is nothing to be in step with: the take goes
  // exactly where the playhead was.
  if (playStartedAt === null) return playheadAt;
  return playheadAt + Math.max(0, recorderStartedAt - playStartedAt) / 1000;
}

export const canRecord = (): boolean =>
  typeof navigator !== "undefined" &&
  typeof navigator.mediaDevices?.getUserMedia === "function" &&
  typeof MediaRecorder !== "undefined";

export interface DeviceChoice {
  camera?: string;
  mic?: string;
}

export interface DeviceList {
  cameras: MediaDeviceInfo[];
  mics: MediaDeviceInfo[];
}

/**
 * Open the devices. A camera with no microphone to go with it is still worth
 * recording, so if the pair cannot be had the camera is tried on its own.
 */
export async function openStream(mode: RecordMode, choice: DeviceChoice): Promise<MediaStream> {
  const audio: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    ...(choice.mic ? { deviceId: { exact: choice.mic } } : {}),
  };
  if (mode === "mic") return navigator.mediaDevices.getUserMedia({ audio });
  const video: MediaTrackConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 },
    ...(choice.camera ? { deviceId: { exact: choice.camera } } : {}),
  };
  try {
    return await navigator.mediaDevices.getUserMedia({ video, audio });
  } catch (error) {
    if ((error as DOMException)?.name !== "NotFoundError") throw error;
    return navigator.mediaDevices.getUserMedia({ video });
  }
}

/** Labels only appear once a stream has been granted, so ask after opening one. */
export async function listDevices(): Promise<DeviceList> {
  const all = await navigator.mediaDevices.enumerateDevices();
  return {
    cameras: all.filter((device) => device.kind === "videoinput"),
    mics: all.filter((device) => device.kind === "audioinput"),
  };
}

export function stopStream(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

export const permissionMessage = (error: unknown): string => {
  const name = (error as DOMException)?.name;
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "The browser did not allow the camera or microphone. Allow it in the address bar and try again.";
  }
  if (name === "NotFoundError") return "No camera or microphone was found on this device.";
  if (name === "NotReadableError") return "The camera or microphone is in use by another app.";
  return error instanceof Error ? error.message : "Could not open the camera or microphone.";
};

export interface Take {
  blob: Blob;
  /** performance.now() when the recorder reported it had begun. */
  startedAt: number;
  /** Seconds, by the wall clock. */
  duration: number;
}

/**
 * One recording from one stream. Chunks are kept in memory until stop, because
 * a WebM's duration can only be written once the whole thing is known.
 */
export class Capture {
  private readonly recorder: MediaRecorder;
  private readonly chunks: Blob[] = [];
  private startedAt = 0;

  constructor(stream: MediaStream, private readonly container: Container) {
    this.recorder = new MediaRecorder(stream, { mimeType: container.mimeType });
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
  }

  /** Resolves with the moment recording actually began. */
  start(): Promise<number> {
    return new Promise((resolve, reject) => {
      this.recorder.onstart = () => {
        this.startedAt = performance.now();
        resolve(this.startedAt);
      };
      this.recorder.onerror = () => reject(new Error("Recording failed to start"));
      // A timeslice keeps memory from being one giant blob and means a crash
      // mid-way loses seconds rather than everything.
      this.recorder.start(1000);
    });
  }

  stop(): Promise<Take> {
    return new Promise((resolve, reject) => {
      const stoppedAt = performance.now();
      this.recorder.onstop = async () => {
        const duration = Math.max(0, (stoppedAt - this.startedAt) / 1000);
        const raw = new Blob(this.chunks, { type: this.container.mimeType.split(";")[0] });
        try {
          resolve({ blob: await withDuration(raw, duration), startedAt: this.startedAt, duration });
        } catch {
          resolve({ blob: raw, startedAt: this.startedAt, duration });
        }
      };
      this.recorder.onerror = () => reject(new Error("Recording failed"));
      if (this.recorder.state === "inactive") reject(new Error("Nothing was recording"));
      else this.recorder.stop();
    });
  }
}

export interface Saved {
  file: File;
  /** Only from the save picker. What lets the project reopen the file later. */
  handle?: FileSystemFileHandle;
  /** Where the bytes went, for the message afterwards. */
  how: "picker" | "download";
}

type SaveWindow = Window & {
  showSaveFilePicker?: (options: {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<FileSystemFileHandle>;
};

/**
 * Write the recording to the user's disk.
 *
 * The picker is preferred because it is the only route to a handle. Declining
 * it, or a browser without it, falls back to a download: the recording is
 * still safe in a folder, and the project asks for it again on reopen like it
 * does for any other file.
 */
export async function saveRecording(blob: Blob, name: string, extension: string): Promise<Saved> {
  const picker = (window as SaveWindow).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({
        suggestedName: name,
        types: [{ description: "Recording", accept: { [blob.type]: [`.${extension}`] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { file: await handle.getFile(), handle, how: "picker" };
    } catch {
      // Cancelled, or no user gesture left to ask with. Downloading is the
      // next best place for it; discarding a take is never the right answer.
    }
  }
  const file = new File([blob], name, { type: blob.type, lastModified: Date.now() });
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { file, how: "download" };
}

/**
 * Describe a recording the way probeFile describes a dropped file. Nothing is
 * probed: the recorder knows the duration, the stream knows the picture size,
 * and a WebM straight from the recorder would fail the probe anyway.
 */
export async function recordingRef(
  file: File,
  mode: RecordMode,
  duration: number,
  picture: { width: number; height: number } | null,
  hasAudio: boolean,
  poster?: string,
): Promise<SourceRef> {
  return {
    id: newId("s"),
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    duration,
    ...(mode === "camera" && picture ? { width: picture.width, height: picture.height } : {}),
    hasVideo: mode === "camera",
    hasAudio,
    fingerprint: await fingerprintFile(file),
    ...(poster ? { poster } : {}),
  };
}
